'use client';

import { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/nextjs';
import { get, logger } from '@brahma/utils';
import useGlobalContext from '$hooks/use-global-context';
import { API_URLS } from '$constants/apis';
import { isPwaServiceWorkerEnabled, isBenignServiceWorkerError, registerServiceWorker, getServiceWorkerPaths } from './pwa-register.helper';
const logRegistrationError = (message: string, error: unknown, context?: Record<string, string>) => {
  if (isBenignServiceWorkerError(error)) {
    return;
  }
  const errorMessage = error instanceof Error ? error.message : String(error);
  logger.error({
    message,
    data: JSON.stringify({
      ...context,
      ...getServiceWorkerPaths(),
      error: errorMessage
    })
  }, true);
  Sentry.captureException(error instanceof Error ? error : new Error(errorMessage), {
    tags: {
      component: 'PwaRegister'
    },
    extra: {
      ...context,
      ...getServiceWorkerPaths()
    }
  });
};

/**
 * Registers the service worker so next-pwa can cache static JS/CSS (StaleWhileRevalidate).
 * next-pwa generates sw.js at build time; we register manually because register: false in next.config
 * (App Router does not auto-register).
 *
 * Uses activeFeatureConfig from global context (SSR). If SSR could not load config, falls back to a
 * client-side /api fetch so unauthenticated routes (e.g. sign-in) still register when the flag is on.
 */
export default function PwaRegister() {
  const {
    state
  } = useGlobalContext();
  const hasRegisteredRef = useRef(false);
  const isPwaEnabled = isPwaServiceWorkerEnabled(state.activeFeatureConfig);
  const hasFeatureConfig = Boolean(state.activeFeatureConfig && Object.keys(state.activeFeatureConfig).length > 0);
  useEffect(() => {
    if (hasRegisteredRef.current) {
      return;
    }
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }

    // Flag explicitly off and config is present — nothing to do.
    if (!isPwaEnabled && hasFeatureConfig) {
      return;
    }
    let cancelled = false;
    const resolveAndRegister = async () => {
      let enabled = isPwaEnabled;
      if (!enabled && !hasFeatureConfig) {
        try {
          const clientConfig = await get(API_URLS.getActiveFeatureConfig);
          enabled = isPwaServiceWorkerEnabled(clientConfig);
        } catch (error) {
          if (!cancelled) {
            logRegistrationError('Failed to fetch active feature config for service worker registration', error);
          }
          return;
        }
      }
      if (!enabled || cancelled) {
        return;
      }
      try {
        const registration = await registerServiceWorker();
        hasRegisteredRef.current = true;
        if (cancelled) {
          return;
        }
        logger.info({
          message: 'Service worker registered',
          data: JSON.stringify({
            ...getServiceWorkerPaths(),
            scriptURL: registration.active?.scriptURL ?? registration.installing?.scriptURL
          })
        }, true);
      } catch (error) {
        if (!cancelled) {
          logRegistrationError('Service worker registration failed', error);
        }
      }
    };
    void resolveAndRegister();
    return () => {
      cancelled = true;
    };
  }, [isPwaEnabled, hasFeatureConfig]);
  return null;
}